import { Avatar } from "@mui/material";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ReactComponent as WhatsApp } from "../../assets/social-icons/whatsapp.svg";
import UserProfileLink from "../../components/ui/UserProfileLink";

import { formatPhoneNumber } from "../../utils/helpers";
const PhoneLink = ({ phoneNumber }) => (
  <a
    href={`https://wa.me/${phoneNumber?.replace(/[^\d]/g, "")}`}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center gap-1 text-primary hover:underline"
  >
    <WhatsApp className="w-5 h-4" /> {formatPhoneNumber(phoneNumber)}
  </a>
);

const UserDetailsCard = ({
  authRole,
  createdBy,
  workOrderManager,
  offersCount,
  engineer,
  engineerCompany,
  routedEngineer,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const isEngineerDeleted = !!engineer?.isDeletedUser;
  // Legacy deletions carry the system account, whose name already reads "Deleted
  // User" — tagging that would be redundant. Only tag when a real name is shown.
  const engineerName = `${engineer?.firstName ?? ""} ${engineer?.lastName ?? ""}`
    .trim()
    .toLowerCase();
  const showDeletedTag = isEngineerDeleted && engineerName !== "deleted user";
  const DeletedTag = () =>
    showDeletedTag ? (
      <span className="mt-1 inline-block rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-normal text-gray-500">
        deleted user
      </span>
    ) : null;

  return (
    <div className="h-full w-full flex flex-col">
      <div className="shadow-md rounded-md py-3 px-4 min-h-0 lg:min-h-24 flex flex-col justify-between">
        <div className="flex">
          <Avatar
            src={
              authRole === "FREELANCER"
                ? (workOrderManager?.profilePicture ?? createdBy?.profilePicture)
                : (engineer?.profilePicture ?? null)
            }
            sx={{ width: 36, height: 36 }}
          />

          {authRole === "FREELANCER" && (
            <div className="ml-2">
              <p className="font-normal text-dark text-sm">Project Manager</p>
              <UserProfileLink
                userId={workOrderManager?.id ?? createdBy?.id}
                className="text-sm text-primary capitalize w-fit"
                linkClassName="cursor-pointer hover:underline"
              >
                {workOrderManager
                  ? `${workOrderManager.firstName} ${workOrderManager.lastName}`
                  : `${createdBy?.firstName} ${createdBy?.lastName}`}
              </UserProfileLink>
            </div>
          )}

          {(authRole === "CLIENT" || authRole === "CUSTOMER") && (
            <div className="ml-2 flex-1">
              {engineer ? (
                <div>
                  {engineerCompany && authRole !== "CUSTOMER" ? (
                    <>
                      <UserProfileLink
                        userId={engineerCompany.owner?.id}
                        className="text-primary font-normal text-sm w-fit"
                        linkClassName="cursor-pointer hover:underline"
                      >
                        {engineerCompany.name}
                        <span className="ml-1 text-[#686868] text-sm">
                          ID: {engineerCompany.id}
                        </span>
                      </UserProfileLink>
                      {/* No id means no link: UserProfileLink renders plain text, so a
                          deleted engineer stays unclickable without a second branch. */}
                      <UserProfileLink
                        userId={isEngineerDeleted ? undefined : engineer.id}
                        search="?direct=true"
                        className={isEngineerDeleted ? "text-gray-500" : "text-primary w-fit"}
                        linkClassName="cursor-pointer hover:underline"
                      >
                        {engineer.firstName} {engineer.lastName}
                        {!isEngineerDeleted && (
                          <span className="ml-1 text-[#686868] text-sm">ID: {engineer.id}</span>
                        )}
                      </UserProfileLink>
                      <DeletedTag />
                      {!isEngineerDeleted && engineer.phoneNumber && (
                        <p className="text-primary">
                          <PhoneLink phoneNumber={engineer.phoneNumber} />
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="text-dark font-normal text-sm">Engineer</p>
                      <UserProfileLink
                        userId={isEngineerDeleted ? undefined : engineer.id}
                        search="?direct=true"
                        className={isEngineerDeleted ? "text-gray-500" : "text-primary w-fit"}
                        linkClassName="cursor-pointer hover:underline"
                      >
                        {engineer.firstName} {engineer.lastName}
                        {!isEngineerDeleted && (
                          <span className="ml-1 text-[#686868] text-sm">ID: {engineer.id}</span>
                        )}
                      </UserProfileLink>
                      <DeletedTag />
                      {!isEngineerDeleted && authRole !== "CUSTOMER" && engineer.phoneNumber && (
                        <p className="text-primary">
                          <PhoneLink phoneNumber={engineer.phoneNumber} />
                        </p>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <p className="font-normal text-dark text-sm">Engineer: Unassigned</p>
              )}

              {authRole !== "CUSTOMER" && !engineer && !engineerCompany && (
                <p
                  className="text-primary capitalize cursor-pointer"
                  onClick={() => navigate(`/workOrder/providers/${id}?tab=pending`)}
                >
                  {offersCount?.requested === 0 &&
                    offersCount?.counter === 0 &&
                    offersCount?.routed === 0 &&
                    "No Request"}
                  {offersCount?.requested !== 0 && offersCount?.requested + " Requests, "}
                  {offersCount?.counter !== 0 && offersCount?.counter + " Counters, "}
                  {offersCount?.routed !== 0 && offersCount?.routed + " Routed"}
                </p>
              )}
            </div>
          )}
        </div>

        {routedEngineer && (
          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-zinc-100">
            <Avatar sx={{ width: 28, height: 28 }} />
            <div>
              <p className="font-normal text-dark text-sm">
                {authRole === "FREELANCER" ? "Assigned Engineer" : "Field Engineer"}
              </p>
              <UserProfileLink
                userId={routedEngineer.id}
                search="?direct=true"
                className="text-sm text-primary capitalize w-fit"
                linkClassName="cursor-pointer hover:underline"
              >
                {routedEngineer.firstName} {routedEngineer.lastName}
              </UserProfileLink>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDetailsCard;
